import { useTranslation } from 'react-i18next';
import { Settings, Clock, MessageSquare } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useConfiguracoes, useAtualizarConfiguracoes } from '@/hooks/useConfiguracoes';
import { useAuthContext } from '@/contexts/AuthContext';

/**
 * Pop-up disparado pelo ícone de engrenagem no header da Inbox. Concentra
 * as configurações do atendimento automático (toggle on/off + minutos sem
 * resposta), que antes viviam soltas em /configuracoes.
 *
 * Acesso restrito a admin — atendentes/prestadores não veem o trigger.
 */
export function InboxAutoServicePopover() {
  const { t } = useTranslation();
  const { isAdmin } = useAuthContext();
  const { data: configuracoes } = useConfiguracoes();
  const atualizar = useAtualizarConfiguracoes();

  if (!isAdmin) return null;

  const toggleEnabled = configuracoes?.atendimento_automatico || false;
  const minutos = configuracoes?.minutos_sem_resposta ?? 30;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="painel__icon-btn"
          aria-label={t('settings.autoService.title')}
        >
          <Settings size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <MessageSquare className="h-4 w-4" />
              {t('settings.autoService.title')}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('settings.autoService.description')}
            </p>
            <div className="flex items-center justify-between mt-3">
              <Label htmlFor="inbox-auto-toggle" className="text-sm">
                {t('settings.autoService.toggle')}
              </Label>
              <Switch
                id="inbox-auto-toggle"
                checked={toggleEnabled}
                onCheckedChange={(checked) =>
                  atualizar.mutate({ atendimento_automatico: checked })
                }
              />
            </div>
          </div>

          <div className="pt-4 border-t border-border-soft">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Clock className="h-4 w-4" />
              {t('settings.inactivityTime.title')}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('settings.inactivityTime.description')}
            </p>
            <div className="flex items-center gap-3 mt-3">
              <Input
                type="number"
                min={1}
                value={minutos}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (value > 0) atualizar.mutate({ minutos_sem_resposta: value });
                }}
                className="w-24"
              />
              <span className="text-xs text-muted-foreground">
                {t('settings.inactivityTime.unit')}
              </span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
